// Shared motion system for the public site: one registered ScrollTrigger
// plugin, one Lenis instance (module singletons, guarded against
// StrictMode's double-invoke and double-mount), and a small reveal hook that
// every page worker (wave 2) uses instead of hand-rolling ScrollTrigger
// boilerplate.
import { useLayoutEffect, type RefObject } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Lenis from 'lenis';

/** True when the user asked the OS for reduced motion. Checked once at
 * module load — the prototype never re-checks mid-session, and neither do
 * we. */
export const reduceMotion =
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;

let scrollTriggerRegistered = false;
function ensureScrollTrigger(): void {
  if (scrollTriggerRegistered) return;
  gsap.registerPlugin(ScrollTrigger);
  scrollTriggerRegistered = true;
}

let lenis: Lenis | null = null;
let lenisTickerAdded = false;

/** Creates the one Lenis instance public pages share, synced to
 * ScrollTrigger. Safe to call repeatedly (StrictMode, remounts): returns the
 * existing instance instead of creating a second one. No-op under reduced
 * motion. */
export function ensureLenis(): Lenis | null {
  ensureScrollTrigger();
  if (reduceMotion) return null;
  if (lenis) return lenis;
  try {
    lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    lenis.on('scroll', ScrollTrigger.update);
    if (!lenisTickerAdded) {
      gsap.ticker.add((time) => {
        lenis?.raf(time * 1000);
      });
      gsap.ticker.lagSmoothing(0);
      lenisTickerAdded = true;
    }
  } catch {
    lenis = null;
  }
  return lenis;
}

/** Call once from PublicLayout. Registers ScrollTrigger and brings the
 * shared Lenis instance up. */
export function useMotion(): void {
  useLayoutEffect(() => {
    ensureLenis();
  }, []);
}

/** Call from PublicLayout on every route change: snaps scroll to the top
 * (instantly — the page-turn sweep is already covering the viewport) and
 * tells ScrollTrigger the DOM moved under it. */
export function resetScrollForRouteChange(): void {
  const instance = ensureLenis();
  if (instance) {
    instance.scrollTo(0, { immediate: true });
  } else if (typeof window !== 'undefined') {
    window.scrollTo(0, 0);
  }
  ScrollTrigger.refresh();
}

/** `useLayoutEffect(fn, deps)` but `fn` runs inside a `gsap.context` scoped
 * to `scopeRef.current`, and the context is reverted (killing every tween
 * and ScrollTrigger it created, including ones made with plain selector
 * text) on cleanup. This is the lifecycle every wave-2 animation should go
 * through per build-plan R2: navigating away and back must never leave a
 * stray ScrollTrigger or a tween frozen mid-flight. */
export function useGsapContext(
  scopeRef: RefObject<Element | null>,
  fn: (context: gsap.Context) => void,
  deps: ReadonlyArray<unknown> = [],
): void {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    if (!scopeRef.current) return;
    ensureScrollTrigger();
    const ctx = gsap.context(fn, scopeRef.current);
    return () => ctx.revert();
    // deps is spread by the caller; this hook intentionally re-runs only
    // when the caller's own deps change, not on every render.
  }, deps);
}

/** Fades and rises every `[data-reveal]` element inside `scopeRef`, once,
 * as it scrolls into view — the behaviour the prototype's markup asks for
 * (`data-reveal` is sprinkled through build.py) without it actually being
 * implemented in site.js. Text and art only, matching the rest of the
 * prototype's reveal treatments (e.g. the About page's quill-wipe values).
 * No-op under reduced motion. */
export function useReveal(scopeRef: RefObject<Element | null>): void {
  useGsapContext(
    scopeRef,
    () => {
      if (reduceMotion) return;
      const targets = gsap.utils.toArray<HTMLElement>('[data-reveal]', scopeRef.current ?? undefined);
      targets.forEach((el) => {
        gsap.from(el, {
          y: 28,
          opacity: 0,
          duration: 0.9,
          ease: 'expo.out',
          scrollTrigger: { trigger: el, start: 'top 88%', once: true },
        });
      });
    },
    [scopeRef],
  );
}

export { gsap, ScrollTrigger };
